import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiArrowUp, FiArrowDown, FiX } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";
import {
  fetchExchangeOptions,
  createExchangeOption,
  updateExchangeOption,
  deleteExchangeOption,
} from "../../utils/exchangeApi";
import { fmt, limitPoints, pointsPerUnitOf, smallestUnit, unitsFor } from "../../utils/exchangeRate";

/**
 * Exchange currencies — what event points can be converted into.
 *
 * Global: set a currency up once — name, colour, decimals, for crypto the
 * "where do we send it" guide the app shows, and a default price and limits.
 * Every event uses the default unless its own Exchange tab sets a rate; caps,
 * budget and KYC are only ever set per event.
 */

const EMPTY_CONFIG = {
  transfer_in: "",
  hint_text: "",
  info_title: "",
  app_name: "",
  app_link: "",
  open_app_button_text: "",
  guide_note: "",
  guide_steps: [],
};

const EMPTY = {
  id: "",
  symbol_name: "",
  display_name: "",
  color: "#26A17B",
  decimals: 4,
  network: "",
  tag: "",
  is_crypto: true,
  is_enabled: true,
  sort_order: 0,
  description: "",
  recipient_config: EMPTY_CONFIG,
  // Default rate and limits; an event's own rate overrides them. The rate is
  // entered as "rate_points event points = rate_units <coin>"; min and max are
  // amounts of the coin ("1 PX", "500 USDT").
  rate_points: "",
  rate_units: 1,
  min_exchange_units: "",
  max_exchange_units: "",
  fee_percentage: 0,
};

const orNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/**
 * The rate in one sentence. The price of 1 coin is added only when the boxes
 * don't already say it ("1000 EP = 0.5 USDT" -> "1 USDT = 2,000 EP").
 */
const rateSentence = (ratePoints, rateUnits, perUnit, symbol) => {
  const sentence = `User gets ${fmt(rateUnits, 8)} ${symbol} for every ${fmt(ratePoints)} event points`;
  return Number(rateUnits) === 1 ? sentence : `${sentence} (1 ${symbol} = ${fmt(perUnit, 6)} event points)`;
};

/** Plain-language previews of the rate, the limits and the fee, as the user will get them. */
const ratePreview = (form, symbol) => {
  const perUnit = pointsPerUnitOf(form.rate_points, form.rate_units);
  const decimals = Number(form.decimals) || 0;
  if (!perUnit) return { perUnit: null };

  const fee = Number(form.fee_percentage) || 0;
  const limits = limitPoints(perUnit, form.min_exchange_units, form.max_exchange_units, decimals);
  const sample = Math.max(limits.minPoints, Number(form.rate_points) || limits.minPoints);
  const received = unitsFor(sample, perUnit, fee, decimals);

  return {
    perUnit,
    min: `= ${fmt(limits.minPoints)} event points${form.min_exchange_units === "" ? ` (smallest amount, ${fmt(limits.minUnits, decimals)} ${symbol})` : ""}`,
    max: limits.maxPoints == null ? "No limit" : `= ${fmt(limits.maxPoints)} event points`,
    fee: fee ? `${fee}% of what the user receives` : "No fee",
    example: `Example: a user exchanging ${fmt(sample)} event points receives ${fmt(received, decimals)} ${symbol}${fee ? ` (after the ${fee}% fee)` : ""}.`,
  };
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-gray-600">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-gray-400 mt-0.5">{hint}</span>}
  </label>
);

const inputClass =
  "mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50";

const CurrencyModal = ({ initial, onClose, onSaved }) => {
  const editing = !!initial;
  const [form, setForm] = useState(() =>
    initial
      ? {
          ...EMPTY,
          ...initial,
          // Inputs need "" rather than null for "no value". A saved price is
          // shown as "<points per 1> event points = 1 <symbol>".
          rate_points: initial.points_per_unit ?? "",
          rate_units: 1,
          min_exchange_units: initial.min_exchange_units ?? "",
          max_exchange_units: initial.max_exchange_units ?? "",
          fee_percentage: initial.fee_percentage ?? 0,
          recipient_config: { ...EMPTY_CONFIG, ...(initial.recipient_config || {}) },
        }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setConfig = (key, value) => setForm((f) => ({ ...f, recipient_config: { ...f.recipient_config, [key]: value } }));
  const steps = form.recipient_config.guide_steps || [];
  const setSteps = (next) => setConfig("guide_steps", next);
  const symbol = (form.symbol_name || "").trim().toUpperCase() || "coins";
  const preview = ratePreview(form, symbol);
  const perUnit = pointsPerUnitOf(form.rate_points, form.rate_units);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rate_points !== "" && !perUnit) {
      return toast.error(`Enter how many ${symbol} that many event points buy`);
    }
    const body = {
      symbol_name: form.symbol_name.trim().toUpperCase(),
      display_name: form.display_name.trim(),
      color: form.color,
      decimals: Number(form.decimals),
      network: form.network || null,
      tag: form.tag || null,
      is_crypto: !!form.is_crypto,
      is_enabled: !!form.is_enabled,
      sort_order: Number(form.sort_order) || 0,
      description: form.description || null,
      points_per_unit: perUnit,
      min_exchange_units: orNull(form.min_exchange_units),
      max_exchange_units: orNull(form.max_exchange_units),
      fee_percentage: Number(form.fee_percentage) || 0,
      recipient_config: form.is_crypto
        ? {
            ...form.recipient_config,
            app_link: form.recipient_config.app_link || null,
            guide_steps: steps.map((s) => s.trim()).filter(Boolean),
          }
        : null,
    };
    if (!editing) body.id = form.id.trim().toLowerCase();

    setSaving(true);
    try {
      const res = editing ? await updateExchangeOption(initial.id, body) : await createExchangeOption(body);
      toast.success(res.message || "Saved");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-base font-bold text-gray-800">{editing ? `Edit ${initial.symbol_name}` : "Add currency"}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <FiX />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="ID" hint="What the app sends, e.g. usdt. Cannot change later.">
              <input
                required
                disabled={editing}
                value={form.id}
                onChange={(e) => set("id", e.target.value)}
                pattern="[a-z0-9_\-]{2,20}"
                placeholder="usdt"
                className={inputClass}
              />
            </Field>
            <Field label="Symbol">
              <input required value={form.symbol_name} onChange={(e) => set("symbol_name", e.target.value)} placeholder="USDT" className={inputClass} />
            </Field>
            <Field label="Display name">
              <input required value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Tether USD" className={inputClass} />
            </Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Colour">
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={form.color.slice(0, 7)} onChange={(e) => set("color", e.target.value.toUpperCase())} className="h-9 w-10 rounded border border-gray-200" />
                <input value={form.color} onChange={(e) => set("color", e.target.value)} className={`${inputClass} mt-0 font-mono`} />
              </div>
            </Field>
            <Field label="Decimals" hint="Amounts round down to this.">
              <input type="number" min={0} max={8} value={form.decimals} onChange={(e) => set("decimals", e.target.value)} className={inputClass} />
            </Field>
            <Field label="Sort order">
              <input type="number" min={0} value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={inputClass} />
            </Field>
            <Field label="Type">
              <select
                disabled={editing && initial.orders_count > 0}
                value={form.is_crypto ? "crypto" : "px"}
                onChange={(e) => set("is_crypto", e.target.value === "crypto")}
                className={inputClass}
              >
                <option value="crypto">Crypto (sent by admin)</option>
                <option value="px">In-app PX (instant)</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Network">
              <input value={form.network || ""} onChange={(e) => set("network", e.target.value)} placeholder="TRC-20" className={inputClass} />
            </Field>
            <Field label="Tag">
              <input value={form.tag || ""} onChange={(e) => set("tag", e.target.value)} placeholder="USD Stablecoin" className={inputClass} />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={inputClass} />
          </Field>

          <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50/60 space-y-3">
            <div>
              <p className="text-xs font-bold text-gray-700">Default price &amp; limits</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                Every event uses these unless its Exchange tab sets its own. Leave the price empty to price this
                currency per event only.
              </p>
            </div>
            {/* The rate the way it is said out loud, top to bottom:
                "10000 event points" = "1 PX". */}
            <div className="max-w-sm space-y-2">
              <p className="text-xs font-bold text-gray-700">Exchange rate</p>
              <Field label="Event points">
                <input
                  type="number"
                  min={0}
                  step="any"
                  aria-label="Event points"
                  value={form.rate_points}
                  onChange={(e) => set("rate_points", e.target.value)}
                  placeholder="e.g. 10000"
                  className={inputClass}
                />
              </Field>
              <Field label={`= ${symbol} given`}>
                <input
                  type="number"
                  min={0}
                  step="any"
                  aria-label={`${symbol} given`}
                  value={form.rate_units}
                  onChange={(e) => set("rate_units", e.target.value)}
                  placeholder="e.g. 1"
                  className={inputClass}
                />
              </Field>
              <p className="text-[11px] font-medium text-gray-500" data-testid="rate-summary">
                {preview.perUnit
                  ? rateSentence(form.rate_points, form.rate_units, preview.perUnit, symbol)
                  : "Leave empty to price this currency per event only"}
              </p>
            </div>

            {/* Limits in the coin itself: "min 1 PX, max 500 USDT". */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={`Min ${symbol} per exchange`} hint={preview.min}>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={form.min_exchange_units}
                  onChange={(e) => set("min_exchange_units", e.target.value)}
                  placeholder="Smallest amount"
                  className={inputClass}
                />
              </Field>
              <Field label={`Max ${symbol} per exchange`} hint={preview.max}>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={form.max_exchange_units}
                  onChange={(e) => set("max_exchange_units", e.target.value)}
                  placeholder="No limit"
                  className={inputClass}
                />
              </Field>
              <Field label="Exchange fee %" hint={preview.fee}>
                <input type="number" min={0} max={99.99} step="any" value={form.fee_percentage} onChange={(e) => set("fee_percentage", e.target.value)} className={inputClass} />
              </Field>
            </div>

            {preview.example && (
              <p className="text-[11px] text-gray-600 bg-white border border-gray-100 rounded-xl px-3 py-2" data-testid="rate-example">
                {preview.example}
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
            <input type="checkbox" checked={!!form.is_enabled} onChange={(e) => set("is_enabled", e.target.checked)} />
            Enabled (off hides it from every event)
          </label>

          {form.is_crypto && (
            <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50/60 space-y-4">
              <p className="text-xs font-bold text-gray-700">Recipient — what the app asks the user for</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Field label">
                  <input required value={form.recipient_config.transfer_in} onChange={(e) => setConfig("transfer_in", e.target.value)} placeholder="Binance ID / Address" className={inputClass} />
                </Field>
                <Field label="Placeholder">
                  <input required value={form.recipient_config.hint_text} onChange={(e) => setConfig("hint_text", e.target.value)} placeholder="Enter your Binance ID or USDT address" className={inputClass} />
                </Field>
                <Field label="Guide title">
                  <input value={form.recipient_config.info_title || ""} onChange={(e) => setConfig("info_title", e.target.value)} placeholder="How to Find Your Binance ID" className={inputClass} />
                </Field>
                <Field label="App name">
                  <input value={form.recipient_config.app_name || ""} onChange={(e) => setConfig("app_name", e.target.value)} placeholder="Binance" className={inputClass} />
                </Field>
                <Field label="App link">
                  <input type="url" value={form.recipient_config.app_link || ""} onChange={(e) => setConfig("app_link", e.target.value)} placeholder="https://www.binance.com" className={inputClass} />
                </Field>
                <Field label="Open-app button text">
                  <input value={form.recipient_config.open_app_button_text || ""} onChange={(e) => setConfig("open_app_button_text", e.target.value)} placeholder="Open Binance App" className={inputClass} />
                </Field>
              </div>
              <Field label="Guide note">
                <textarea rows={2} value={form.recipient_config.guide_note || ""} onChange={(e) => setConfig("guide_note", e.target.value)} className={inputClass} />
              </Field>

              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600">Guide steps</span>
                  <button type="button" onClick={() => setSteps([...steps, ""])} className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1">
                    <FiPlus size={12} /> Add step
                  </button>
                </div>
                <div className="space-y-2 mt-2">
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-2 text-[10px] font-bold text-gray-400 w-4">{i + 1}.</span>
                      <textarea
                        rows={1}
                        value={step}
                        onChange={(e) => setSteps(steps.map((s, j) => (j === i ? e.target.value : s)))}
                        className={`${inputClass} mt-0`}
                      />
                      <div className="flex flex-col">
                        <button type="button" disabled={i === 0} onClick={() => { const next = [...steps]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; setSteps(next); }} className="p-1 text-gray-400 disabled:opacity-30">
                          <FiArrowUp size={12} />
                        </button>
                        <button type="button" disabled={i === steps.length - 1} onClick={() => { const next = [...steps]; [next[i + 1], next[i]] = [next[i], next[i + 1]]; setSteps(next); }} className="p-1 text-gray-400 disabled:opacity-30">
                          <FiArrowDown size={12} />
                        </button>
                      </div>
                      <button type="button" onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 rounded-xl hover:bg-gray-100">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ExchangeCurrencies = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("exchange", "create");
  const canEdit = hasPermission("exchange", "edit");
  const canDelete = hasPermission("exchange", "delete");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchExchangeOptions();
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load currencies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (row) => {
    const words = row.orders_count > 0
      ? `${row.symbol_name} has ${row.orders_count} order(s), so it will be switched off, not deleted. Continue?`
      : `Delete ${row.symbol_name}? Its rates on every event go with it.`;
    if (!window.confirm(words)) return;
    try {
      const res = await deleteExchangeOption(row.id);
      toast.success(res.message || "Done");
      load();
    } catch (err) {
      toast.error(err.message || "Could not delete");
    }
  };

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-6xl mx-auto space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiDollarSign className="text-blue-600" /> Exchange Currencies
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  What event points can be exchanged for, and at what default price. An event's Exchange tab can set
                  its own price and limits, a per-user cap, a budget and KYC.
                </p>
              </div>
              {canCreate && (
                <button onClick={() => setEditing(null)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl inline-flex items-center gap-1.5">
                  <FiPlus /> Add currency
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Currency</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Network</th>
                    <th className="text-left px-4 py-3">Default price</th>
                    <th className="text-left px-4 py-3">Min / Max per exchange</th>
                    <th className="text-right px-4 py-3">Decimals</th>
                    <th className="text-right px-4 py-3" title="Events that set their own rate for this currency">Event rates</th>
                    <th className="text-right px-4 py-3">Orders</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-gray-400">No currencies yet.</td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="w-3 h-3 rounded-full" style={{ background: row.color }} />
                          <div>
                            <div className="font-bold text-gray-800">{row.symbol_name}</div>
                            <div className="text-[10px] text-gray-400">{row.display_name} · {row.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{row.is_crypto ? "Crypto" : "In-app PX"}</td>
                      <td className="px-4 py-3 text-gray-600">{row.network || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.points_per_unit ? (
                          <span className="font-medium text-gray-800">
                            {fmt(row.points_per_unit)} event points = 1 {row.symbol_name}
                          </span>
                        ) : (
                          <span className="text-gray-400">Per event only</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {row.points_per_unit ? (
                          <>
                            {fmt(row.min_exchange_units ?? smallestUnit(row.decimals), row.decimals)} /{" "}
                            {row.max_exchange_units != null ? fmt(row.max_exchange_units, row.decimals) : "No limit"} {row.symbol_name}
                            <span className="text-gray-400"> · {fmt(row.fee_percentage, 2)}% fee</span>
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{row.decimals}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{row.events_count}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{row.orders_count}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-md border text-[10px] font-semibold ${row.is_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                          {row.is_enabled ? "Enabled" : "Off"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canEdit && (
                          <button onClick={() => setEditing(row)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" title="Edit">
                            <FiEdit2 size={13} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => remove(row)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Delete">
                            <FiTrash2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {editing !== undefined && (
        <CurrencyModal
          initial={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ExchangeCurrencies;
